import { useForm } from "react-hook-form";

import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import useNotification from "../notifications/useNotifications";
import { ICreatePartRequest, IPartRequest, PartRequestContactPreference } from "../../models/partRequests/IPartRequest";
import { PartRequestFormValues, toCreatePartRequest, validatePartRequestForm } from "../../utils/partRequests";

interface CreatePartRequestFormProps {
  /** When editing, the existing request pre-fills the form. */
  existing?: IPartRequest;
  onSubmit: (request: ICreatePartRequest) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const contactOptions = [
  { id: PartRequestContactPreference.Phone, name: "Phone" },
  { id: PartRequestContactPreference.Email, name: "Email" },
];

function toFormValues(existing?: IPartRequest): PartRequestFormValues {
  return {
    customerName: existing?.customerName ?? "",
    customerPhone: existing?.customerPhone ?? "",
    customerEmail: existing?.customerEmail ?? "",
    contactPreference: existing?.contactPreference ?? PartRequestContactPreference.Phone,
    vrm: existing?.vrm ?? "",
    make: existing?.make ?? "",
    model: existing?.model ?? "",
    year: existing?.year ? String(existing.year) : "",
    partWanted: existing?.partWanted ?? "",
    details: existing?.details ?? "",
  };
}

export default function CreatePartRequestForm({ existing, onSubmit, onCancel, isLoading }: CreatePartRequestFormProps) {
  const { addNotification } = useNotification();
  const { register, handleSubmit, setValue, watch } = useForm<PartRequestFormValues>({
    defaultValues: toFormValues(existing),
  });

  const submit = (values: PartRequestFormValues) => {
    const problem = validatePartRequestForm(values);
    if (problem) {
      addNotification({ variant: "warn", primaryText: problem });
      return;
    }
    onSubmit(toCreatePartRequest(values));
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6 p-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The part</h3>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput label="Part wanted" register={register} registerName="partWanted" required cols="sm:col-span-2" placeholder="e.g. Driver side front door, alternator, N/S rear light" />
          <TextInput label="Details" register={register} registerName="details" richText rows={3} cols="sm:col-span-2" placeholder="Colour, trim, engine code, anything that narrows it down" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The vehicle</h3>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <TextInput label="Registration" register={register} registerName="vrm" placeholder="AB12 CDE" customClasses="uppercase" />
          <TextInput label="Make" register={register} registerName="make" />
          <TextInput label="Model" register={register} registerName="model" />
          <TextInput label="Year" register={register} registerName="year" type="number" />
        </div>
        <p className="mt-1 text-xs text-gray-500">A registration or at least the make is needed so we can match vehicles in the yard.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The customer</h3>
        <p className="text-xs text-gray-500">Kept in Wilma only - never shared if the request goes out to the network.</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput label="Name" register={register} registerName="customerName" required />
          <SelectInput label="Prefers" register={register} registerName="contactPreference" options={contactOptions} setValue={setValue} watch={watch} />
          <TextInput label="Phone" register={register} registerName="customerPhone" type="tel" />
          <TextInput label="Email" register={register} registerName="customerEmail" type="email" />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <ButtonInput label="Cancel" classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300" onClick={onCancel} />
        <ButtonInput label={existing ? "Save changes" : "Log request"} isSubmit onClick={undefined} classes="" isLoading={isLoading} />
      </div>
    </form>
  );
}
